import { readGclid } from 'utils/attribution/gclid';
import { readLanding } from 'utils/attribution/landing';
import { readEffectiveUTM } from 'utils/attribution/utm';

const hasAnyUtm = (utm: Record<string, string | undefined>) =>
  Boolean(
    utm.utm_source ||
    utm.utm_medium ||
    utm.utm_campaign ||
    utm.utm_campaign_name ||
    utm.utm_campaign_id ||
    utm.utm_term ||
    utm.utm_content,
  );

const MAX_FIELD_LEN = 200;
const MAX_URL_FIELD_LEN = 500;

const safeStr = (v: unknown, max: number = MAX_FIELD_LEN): string => {
  const s = typeof v === 'string' ? v : '';
  if (!s) {
    return '';
  }

  return s.length > max ? s.slice(0, max) : s;
};

// go.prismatic.io is the campaign-redirect/ABM landing app: a hop through it
// must not read as an external referral (Nicole's "referral with no UTMs" leads).
const SELF_DOMAINS = [
  'prismatic.io',
  'www.prismatic.io',
  'lp.prismatic.io',
  'forms.prismatic.io',
  'docs.prismatic.io',
  'go.prismatic.io',
];

const isSelfReferral = (hostname: string): boolean => SELF_DOMAINS.includes(hostname.toLowerCase());

const getWebTrafficSource = (referrer: string, utmSource?: string): string => {
  if (utmSource && utmSource.trim().length) {
    return utmSource.trim();
  }

  if (!referrer || !referrer.trim().length) {
    return 'Direct';
  }

  try {
    const hostname = new URL(referrer).hostname;

    return isSelfReferral(hostname) ? 'Direct' : hostname;
  } catch {
    return referrer;
  }
};

/**
 * Hidden fields are read from the persisted campaign, never from the current
 * URL, so a visitor who lands on one page and converts on another still carries
 * their campaign. Empty values are left out entirely rather than sent as blank
 * strings: there is nothing to say about a field we have no value for, and the
 * payload stays small (an oversized one has already broken submissions once).
 */
export const buildMarketoHiddenFields = (): Record<string, string> => {
  const utm = (readEffectiveUTM() || {}) as Record<string, string | undefined>;
  const gclid = safeStr(readGclid() || '');
  const { landing, referrer } = readLanding();

  const safeLanding = safeStr(landing, MAX_URL_FIELD_LEN) || '/';
  const safeReferrer = safeStr(referrer, MAX_URL_FIELD_LEN);

  const campaignValue = utm.utm_campaign_name || utm.utm_campaign || '';
  const webTrafficSource = getWebTrafficSource(safeReferrer, utm.utm_source);

  const fields: Record<string, string> = {
    landing_page: safeLanding,
    Landing_Page__c: safeLanding,
    Web_Traffic_Source__c: safeStr(webTrafficSource),
    // Tells the CRM whether the browser actually held a campaign at submit
    // time, which is what separates "we never captured it" from "it was
    // captured and lost downstream" when a lead lands with no UTMs.
    CDP_Attribution_Synced__c: hasAnyUtm(utm) ? 'true' : 'false',
  };

  const set = (value: string, ...names: string[]): void => {
    if (!value) {
      return;
    }

    names.forEach(name => {
      fields[name] = value;
    });
  };

  set(safeStr(utm.utm_source), 'utm_source', 'UTM_Source__c');
  set(safeStr(utm.utm_medium), 'utm_medium', 'UTM_Medium__c');
  set(safeStr(campaignValue), 'utm_campaign', 'UTM_Campaign__c');
  set(safeStr(utm.utm_campaign_id), 'utm_campaign_id', 'UTM_Campaign_ID__c');
  set(safeStr(utm.utm_campaign_name), 'utm_campaign_name');
  set(safeStr(utm.utm_term), 'utm_term', 'UTM_Term__c');
  set(safeStr(utm.utm_content), 'utm_content', 'UTM_Content__c');

  set(safeReferrer, 'referrer_url', 'Referrer_URL__c');
  set(gclid, 'gclid', 'GCLID', 'gclid__c', 'GCLID__c');

  return fields;
};
